import { LogOut, RefreshCw } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { formatClock, formatDateTime } from '../../lib/time';
import { Button } from '../atoms';
import { ThemeSwitcher, Wordmark } from '../molecules';

interface TopBarProps {
  username: string | null;
  /** When the data on screen was last fetched (ms), 0 if never. */
  updatedAt: number;
  refreshing: boolean;
  onRefresh: () => void;
  onSignOut: () => void;
}

/** Navigation row at the top of the band. */
export function TopBar({ username, updatedAt, refreshing, onRefresh, onSignOut }: TopBarProps) {
  return (
    <div className="flex h-16 items-center gap-3 border-b border-band-line sm:gap-6">
      <Link to="/" className="flex-none no-underline" aria-label="Citilight Monitor, overview">
        <Wordmark />
      </Link>

      <nav aria-label="Main" className="hidden items-center gap-1 md:flex">
        {[
          { to: '/', label: 'Overview', end: true },
          { to: '/servers', label: 'Servers', end: false },
          { to: '/rules', label: 'Rules', end: false },
          { to: '/settings', label: 'Settings', end: false },
        ].map(({ to, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                'rounded-full px-3.5 py-1.5 text-sm font-medium no-underline transition-colors',
                isActive ? 'bg-band-raised text-band-fg' : 'text-band-fg-secondary hover:text-band-fg',
              )
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="ml-auto flex flex-none items-center gap-1 sm:gap-2.5">
        {updatedAt > 0 && (
          <span className="hidden items-center gap-2 text-xs text-band-fg-muted lg:inline-flex" title={`Last refreshed ${formatDateTime(updatedAt)}`}>
            <span className="size-2 rounded-full bg-band-accent" aria-hidden />
            Live · <span className="font-mono">{formatClock(updatedAt)}</span>
          </span>
        )}
        <Button
          variant="band"
          size="icon"
          onClick={onRefresh}
          disabled={refreshing}
          aria-label="Refresh now"
          title="Refresh now (the page also refreshes every minute)"
          icon={<RefreshCw size={15} className={cn(refreshing && 'animate-spin')} aria-hidden />}
        />
        <span className="hidden sm:inline-flex">
          <ThemeSwitcher />
        </span>
        {username && (
          <span
            className="hidden size-9 place-items-center rounded-full bg-[#f2a93b] font-semibold uppercase text-band sm:grid"
            title={`Signed in as ${username}`}
            aria-label={`Signed in as ${username}`}
          >
            {username.slice(0, 1)}
          </span>
        )}
        <Button variant="band" size="icon" onClick={onSignOut} aria-label="Sign out" title="Sign out" icon={<LogOut size={15} aria-hidden />} />
      </div>
    </div>
  );
}
